import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import { supabase } from './utils/supabase';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import Calculator from './pages/Calculator';
import AdminPanel from './pages/AdminPanel';
import OperatorDetail from './pages/OperatorDetail';
import Reports from './pages/Reports';
import Packages from './pages/Packages';
import SupplierDashboard from './pages/SupplierDashboard';
import OperatorDashboard from './pages/OperatorDashboard';
import SupplierIntro from './components/SupplierIntro';
import OperatorIntro from './components/OperatorIntro';
import Login from './pages/Login';

// Role context
export const RoleContext = createContext({ role: null, userRecord: null });
export const useRole = () => useContext(RoleContext);

// NEGroup Microsoft domains = admin
const ADMIN_DOMAINS = ['nextexperience.group'];

function AdminApp() {
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <main className="pt-16 pb-12 px-4 sm:px-6 max-w-7xl mx-auto">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/calculator" element={<Calculator />} />
          <Route path="/calculator/:operatorId" element={<Calculator />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/operator/:operatorId" element={<OperatorDetail />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/packages" element={<Packages />} />
        </Routes>
      </main>
    </div>
  );
}

function SupplierApp() {
  const [showIntro, setShowIntro] = useState(!localStorage.getItem('bc_supplier_intro_seen'));
  const { userRecord } = useRole();
  if (showIntro) return (
    <div className="min-h-screen bg-black">
      <SupplierIntro companyName={userRecord?.company_name} onContinue={() => { localStorage.setItem('bc_supplier_intro_seen', '1'); setShowIntro(false); }} />
    </div>
  );
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <main className="pt-16 pb-12 px-4 sm:px-6 max-w-7xl mx-auto">
        <Routes>
          <Route path="/*" element={<SupplierDashboard />} />
        </Routes>
      </main>
    </div>
  );
}

function OperatorApp() {
  const [showIntro, setShowIntro] = useState(!localStorage.getItem('bc_operator_intro_seen'));
  const { userRecord } = useRole();
  if (showIntro) return (
    <div className="min-h-screen bg-black">
      <OperatorIntro companyName={userRecord?.company_name} onContinue={() => { localStorage.setItem('bc_operator_intro_seen', '1'); setShowIntro(false); }} />
    </div>
  );
  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      <main className="pt-16 pb-12 px-4 sm:px-6 max-w-7xl mx-auto">
        <Routes>
          <Route path="/*" element={<OperatorDashboard />} />
        </Routes>
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [userRecord, setUserRecord] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) detectRole(session.user);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) detectRole(session.user);
      else { setRole(null); setUserRecord(null); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const detectRole = async (user) => {
    const email = user.email || '';
    const domain = email.split('@')[1] || '';

    // Check if NEGroup admin (Microsoft login from NEGroup domain)
    if (ADMIN_DOMAINS.includes(domain)) {
      setRole('admin');
      setUserRecord({ email, role: 'admin', company_name: 'Next Experience Group' });
      setLoading(false);
      return;
    }

    // Check bc_users table for supplier/operator role
    try {
      const { data } = await supabase.from('bc_users').select('*').eq('email', email).single();
      if (data) {
        setRole(data.role);
        setUserRecord(data);
      } else {
        // Unknown user — default to operator view with limited access
        setRole('unknown');
        setUserRecord({ email, role: 'unknown' });
      }
    } catch {
      setRole('unknown');
      setUserRecord({ email, role: 'unknown' });
    }
    setLoading(false);
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="text-brand-gold animate-pulse font-display">Loading...</div></div>;
  if (!session) return <Login />;

  return (
    <RoleContext.Provider value={{ role, userRecord }}>
      <BrowserRouter>
        {role === 'admin' && <Routes><Route path="/*" element={<AdminApp />} /></Routes>}
        {role === 'supplier' && <Routes><Route path="/*" element={<SupplierApp />} /></Routes>}
        {role === 'operator' && <Routes><Route path="/*" element={<OperatorApp />} /></Routes>}
        {role === 'unknown' && (
          <div className="min-h-screen bg-black flex items-center justify-center px-4 text-center">
            <div>
              <p className="text-neutral-400 mb-4">Your account hasn't been set up yet.</p>
              <p className="text-neutral-600 text-sm mb-6">Contact Next Experience Group to get access.</p>
              <button onClick={() => supabase.auth.signOut()} className="btn-secondary text-sm">Sign Out</button>
            </div>
          </div>
        )}
      </BrowserRouter>
    </RoleContext.Provider>
  );
}
